import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import moment from "moment/moment";
import { _get, _post } from "../api/apiClient";
import { HttpStatusCode } from "axios";
import { CustomError } from "../api/CustomError";
import { CustomSuccess } from "../api/CustomSuccess";
import { API_STATUS } from "../rtk-query/features/sliceUtil";
import store, { persistor } from "../app/store";
import { setLoaderFalse, setLoaderTrue } from "./loaderSlice";
import {
  setForceLogout,
  setUnauthorizedForceLogout,
} from "../rtk-query/features/stripe/stripeSlice";
import { productOpportunitiesAnalysisSlice } from "../rtk-query/features/dashboard/productOpportunities/productOpportunitiesAnalysisSlice";
import { depositOpportunitiesAnalysisSlice } from "../rtk-query/features/dashboard/depositOpportunities/depositOpportunitiesAnalysisSlice";
import {
  setIsFHSReset,
  setShowGaugeChart,
  setSkipFHSAPICall,
} from "../rtk-query/features/dashboard/financialHealthScore/financialHealthScoreAnalysisSlice";
import { missedEarningsAnalysisSlice } from "../rtk-query/features/dashboard/missedEarnings/missedEarningsAnalysisSlice";
import { topCompetitorsAnalysisSlice } from "../rtk-query/features/dashboard/topCompetitors/topCompetitorsAnalysisSlice";
import { insightsAndRecommendationSlice } from "../rtk-query/features/insightsAndRecommendation/insightsAndRecommendationSlice";
import { resetContactUs } from "./contactUsSlice";
import { clearPrivileges } from "./privilegesSlice";
import { resetNotifications } from "../rtk-query/features/usernotification/notificationSlice";
import { resetLockedCampaignSlice } from "../rtk-query/features/lockedCampaignSlice";
import { SUBSCRIPTION } from "../utils/global";
import { trendRTK } from "../rtk-query/features/dashboard/trends/trendRTK";
import { newInitialFilterDataSlice } from "./newInitialFilterDataSlice";
import { resetDashboard } from "./dashboardSlice";
import { setIsMarketing, setIsPro } from "./subscriptionSlice";

const loggedOutState = {
  userData: {},
  isLogin: false,
  expiresIn: null,
  expirationTime: null,
  loggedInTime: null,
  userContactedSales: false,
  isLoading: API_STATUS.STATUS_IDLE,
};

const initialState = {
  ...loggedOutState,
  userData: sessionStorage.getItem("userData")
    ? JSON.parse(sessionStorage.getItem("userData"))
    : {},
  isLogin: Boolean(sessionStorage.getItem("isLogin")),
  expiresIn: sessionStorage.getItem("expiresIn") ?? null,
  expirationTime: sessionStorage.getItem("expirationTime") ?? null,
  loggedInTime: sessionStorage.getItem("loggedInTime") ?? null,
};

export const _refreshToken = () => {
  const state = store.getState();
  return state?.authReducer?.userData?.refreshToken;
};

export const ResetReduxState = createAsyncThunk({}, async (__, thunkAPI) => {
  try {
    thunkAPI.dispatch(clearPrivileges());
    thunkAPI.dispatch(setSkipFHSAPICall(false));
    thunkAPI.dispatch(setIsFHSReset(true));
    thunkAPI.dispatch(setShowGaugeChart(false));
    thunkAPI.dispatch(resetContactUs());
    thunkAPI.dispatch(resetNotifications());
    thunkAPI.dispatch(resetLockedCampaignSlice());
    thunkAPI.dispatch(resetDashboard());
    thunkAPI.dispatch(setIsPro(false));
    thunkAPI.dispatch(setIsMarketing(false));
    thunkAPI.dispatch(productOpportunitiesAnalysisSlice.util.resetApiState());
    thunkAPI.dispatch(depositOpportunitiesAnalysisSlice.util.resetApiState());
    thunkAPI.dispatch(missedEarningsAnalysisSlice.util.resetApiState());
    thunkAPI.dispatch(topCompetitorsAnalysisSlice.util.resetApiState());
    thunkAPI.dispatch(insightsAndRecommendationSlice.util.resetApiState());
    thunkAPI.dispatch(trendRTK.util.resetApiState());
    thunkAPI.dispatch(newInitialFilterDataSlice.util.resetApiState());

    return true;
  } catch (err) {
    thunkAPI.dispatch(setLoaderFalse());
    return thunkAPI.rejectWithValue(err);
  }
});

export const LogOut = createAsyncThunk("/auth/logout", async (_, thunkAPI) => {
  try {
    const refreshToken = _refreshToken();
    thunkAPI.dispatch(setLoaderTrue());
    const config = {
      successMessage: "Login Success!",
      errorMessage: "Login Error!",
      disableToast: true,
    };
    const response = await _get(
      `/auth/logout?refreshtoken=${refreshToken}`,
      {},
      { metadata: { apiName: "logout" } },
    );
    if (response?.statusCode !== HttpStatusCode.Ok) {
      throw CustomError(response, config);
    }
    CustomSuccess(response, {
      ...config,
    });
    if (persistor && typeof persistor.purge === "function") {
      await persistor.purge();
    }
    thunkAPI.dispatch(setForceLogout(false));
    thunkAPI.dispatch(setUnauthorizedForceLogout(false));
    thunkAPI.dispatch(logOut());
    await thunkAPI.dispatch(ResetReduxState());
    thunkAPI.dispatch(setLoaderFalse());
    return null;
  } catch (err) {
    thunkAPI.dispatch(setLoaderFalse());
    return thunkAPI.rejectWithValue(err);
  }
});

export const LoginThunk = createAsyncThunk(
  "auth/Login",
  async (data, thunkAPI) => {
    try {
      await thunkAPI.dispatch(ResetReduxState());
      const config = {
        successMessage: "Login Success!",
        errorMessage: "Login Error!",
      };
      const response = await _post(`/auth/login`, data, {
        metadata: { apiName: "login" },
      });

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      const _response = CustomSuccess(response, {
        ...config,
        disableToast: true,
      });
      return _response;
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const authSlice = createSlice({
  name: "authSlice",
  initialState,
  reducers: {
    login: (state, action) => {
      state.userData = action.payload;
      state.isLogin = true;
      state.token = action.payload?.accessToken;
      state.expiresIn = action.payload?.expiresIn;
      state.expirationTime = moment()
        .add(action.payload?.expiresIn, "seconds")
        .format();
      state.loggedInTime = moment().format();
      state.createdOn = action.payload?.createdOn;
      sessionStorage.setItem("userData", JSON.stringify(state.userData));
      sessionStorage.setItem("isLogin", state.isLogin);
      sessionStorage.setItem("expiresIn", state.expiresIn);
      sessionStorage.setItem("expirationTime", state.expirationTime);
      sessionStorage.setItem("loggedInTime", state.loggedInTime);
    },
    setFirstName: (state, action) => {
      state.userData.firstName = action.payload;
      sessionStorage.setItem("userData", JSON.stringify(state.userData));
    },
    setLastName: (state, action) => {
      state.userData.lastName = action.payload;
      sessionStorage.setItem("userData", JSON.stringify(state.userData));
    },
    logOut: (state) => {
      state.userData = {};
      state.isLogin = false;
    },
    setUserContactedSales: (state, action) => {
      state.userContactedSales = action.payload;
    },
    updateSubscriptionDetails: (state, action) => {
      const [subscription = "", subscriptionName = ""] =
        action.payload.productName?.split("|") || [];
      state.userData = {
        ...state.userData,
        productId: action.payload.productId,
        productName: action.payload.productName,
        subscription: SUBSCRIPTION?.ANALYST,
        subscriptionName: subscriptionName,
        subscriptionId: action.payload.subscriptionId,
        productPrivileges: action.payload.productPrivileges,
        privileges: action.payload.privileges,
      };

      sessionStorage.setItem("userData", JSON.stringify(state.userData));
    },
    resetAuthReducer: () => loggedOutState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(LogOut.pending, (state, action) => {})
      .addCase(LogOut.fulfilled, (state, action) => {
        sessionStorage.clear();
        state.userData = {};
        state.isLogin = false;
        state.token = null;
      })
      .addCase(LogOut.rejected, (state, action) => {
        sessionStorage.clear();
        state.userData = {};
        state.isLogin = false;
        state.token = null;
      });
  },
});

// Action creators are generated for each case reducer function
export const {
  login,
  logOut,
  setFirstName,
  setLastName,
  setUserContactedSales,
  updateSubscriptionDetails,
  resetAuthReducer,
} = authSlice.actions;

export default authSlice.reducer;
